<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const UNCLASSIFIED_KEY = 'unclassified';

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

// ============================================================
// Selector de bandeja: cada bandeja WhatsApp/Twilio tiene sus propias
// secciones personalizadas de plantillas con su orden.
// ============================================================
const inboxes = useMapGetter('inboxes/getInboxes');
const whatsappInboxes = computed(() =>
  (inboxes.value || []).filter(
    i => i.channel_type === 'Channel::TwilioSms' && i.medium === 'whatsapp'
  )
);
const selectedInboxId = ref(null);
const inboxOptions = computed(() =>
  whatsappInboxes.value.map(i => ({ label: i.name, value: String(i.id) }))
);
const selectedInboxLabel = computed(() => {
  const inbox = whatsappInboxes.value.find(
    i => i.id === Number(selectedInboxId.value)
  );
  return inbox?.name || t('TEMPLATES_PAGE.SELECT_INBOX');
});

const sections = computed(() =>
  selectedInboxId.value
    ? store.getters['templateClassifications/getSections'](
        Number(selectedInboxId.value)
      )
    : []
);
const allTemplates = computed(() =>
  selectedInboxId.value
    ? store.getters['templateClassifications/getTemplates'](
        Number(selectedInboxId.value)
      )
    : []
);

// Columnas dinámicas: "Sin clasificar" fija + las secciones de la bandeja.
const columns = computed(() => [
  {
    key: UNCLASSIFIED_KEY,
    id: null,
    name: t('TEMPLATES_PAGE.COLUMN_UNCLASSIFIED'),
  },
  ...sections.value.map(s => ({ key: String(s.id), id: s.id, name: s.name })),
]);

// ============================================================
// Alias de visualización (solo admin): renombrar cómo se muestra una plantilla
// en Chatwoot sin tocar su nombre real en Twilio.
// ============================================================
const editingName = ref(null);
const editValue = ref('');

const displayNameOf = el => el.display_name || el.friendly_name || el.name;

const startEdit = el => {
  if (!isAdmin.value) return;
  editingName.value = el.name;
  editValue.value = el.display_name || '';
};

const cancelEdit = () => {
  editingName.value = null;
  editValue.value = '';
};

const saveEdit = async el => {
  if (editingName.value !== el.name) return;
  const value = editValue.value.trim();
  editingName.value = null;
  if ((el.display_name || '') === value) return; // sin cambios
  try {
    await store.dispatch('templateClassifications/rename', {
      inboxId: Number(selectedInboxId.value),
      templateName: el.name,
      displayName: value,
    });
  } catch {
    useAlert(t('TEMPLATES_PAGE.RENAME_ERROR'));
  }
};

// ============================================================
// Secciones: crear (+), renombrar (click en el título) y reordenar (◀ ▶).
// ============================================================
const isAddingSection = ref(false);
const newSectionName = ref('');
const editingSectionId = ref(null);
const sectionEditValue = ref('');

const createSection = async () => {
  const name = newSectionName.value.trim();
  if (!name) return;
  isAddingSection.value = false;
  newSectionName.value = '';
  try {
    await store.dispatch('templateClassifications/createSection', {
      inboxId: Number(selectedInboxId.value),
      name,
    });
    useAlert(t('TEMPLATES_PAGE.SECTION_CREATED', { name }));
  } catch (e) {
    const msg = e?.response?.data?.error || '';
    useAlert(t('TEMPLATES_PAGE.SECTION_CREATE_ERROR', { error: msg }));
  }
};

const startSectionEdit = column => {
  if (!isAdmin.value || !column.id) return;
  editingSectionId.value = column.id;
  sectionEditValue.value = column.name;
};

const cancelSectionEdit = () => {
  editingSectionId.value = null;
  sectionEditValue.value = '';
};

const saveSectionEdit = async column => {
  if (editingSectionId.value !== column.id) return;
  const name = sectionEditValue.value.trim();
  editingSectionId.value = null;
  if (!name || name === column.name) return;
  try {
    await store.dispatch('templateClassifications/renameSection', {
      inboxId: Number(selectedInboxId.value),
      id: column.id,
      name,
    });
  } catch (e) {
    const msg = e?.response?.data?.error || '';
    useAlert(t('TEMPLATES_PAGE.SECTION_RENAME_ERROR', { error: msg }));
  }
};

// Eliminar sección: sus plantillas vuelven a "Sin clasificar" (confirmación
// con Dialog del design system).
const deleteSectionDialogRef = ref(null);
const sectionToDelete = ref(null);

const askDeleteSection = column => {
  if (!isAdmin.value || !column.id) return;
  sectionToDelete.value = column;
  deleteSectionDialogRef.value?.open();
};

const confirmDeleteSection = async () => {
  const section = sectionToDelete.value;
  sectionToDelete.value = null;
  deleteSectionDialogRef.value?.close();
  if (!section) return;
  // eslint-disable-next-line no-console
  console.log('[Templates] delete section', section.id, section.name);
  try {
    await store.dispatch('templateClassifications/destroySection', {
      inboxId: Number(selectedInboxId.value),
      id: section.id,
    });
    useAlert(t('TEMPLATES_PAGE.SECTION_DELETED', { name: section.name }));
  } catch (e) {
    const msg = e?.response?.data?.error || '';
    useAlert(t('TEMPLATES_PAGE.SECTION_DELETE_ERROR', { error: msg }));
  }
};

const moveSection = async (column, delta) => {
  const ids = sections.value.map(s => s.id);
  const from = ids.indexOf(column.id);
  const to = from + delta;
  if (from === -1 || to < 0 || to >= ids.length) return;
  ids.splice(from, 1);
  ids.splice(to, 0, column.id);
  // eslint-disable-next-line no-console
  console.log('[Templates] reorder sections', ids);
  try {
    await store.dispatch('templateClassifications/reorderSections', {
      inboxId: Number(selectedInboxId.value),
      orderedIds: ids,
    });
  } catch {
    useAlert(t('TEMPLATES_PAGE.SECTION_REORDER_ERROR'));
  }
};

const focusInput = el => {
  if (el) el.focus();
};

const isLoading = computed(
  () => store.getters['templateClassifications/getUIFlags'].isFetching
);
const isUpdating = computed(
  () => store.getters['templateClassifications/getUIFlags'].isUpdating
);
const isRefreshing = computed(
  () => store.getters['templateClassifications/getUIFlags'].isRefreshing
);

const search = ref('');

const refreshFromTwilio = async () => {
  try {
    const result = await store.dispatch('templateClassifications/refreshAll');
    useAlert(
      t('TEMPLATES_PAGE.REFRESH_SUCCESS', {
        count: result?.synced_inboxes ?? 0,
      })
    );
  } catch {
    useAlert(t('TEMPLATES_PAGE.REFRESH_ERROR'));
  }
};

const fetchTemplates = async () => {
  if (!selectedInboxId.value) return;
  await store.dispatch('templateClassifications/get', {
    inboxId: Number(selectedInboxId.value),
  });
};

// Local mirror per column to drive the drag & drop. The source of truth stays
// in Vuex but we use refs here so the columns can be mutated freely on drop.
const columnsState = ref({});

const columnKeyOf = tpl =>
  tpl.section_id ? String(tpl.section_id) : UNCLASSIFIED_KEY;

const rebuildColumns = () => {
  const all = allTemplates.value;
  const q = search.value.trim().toLowerCase();
  const filtered = q
    ? all.filter(
        tpl =>
          (tpl.name || '').toLowerCase().includes(q) ||
          (tpl.display_name || '').toLowerCase().includes(q)
      )
    : all;
  const next = {};
  columns.value.forEach(col => {
    next[col.key] = filtered
      .filter(tpl => columnKeyOf(tpl) === col.key)
      .sort((a, b) => (a.position || 0) - (b.position || 0));
  });
  columnsState.value = next;
};

const onColumnChange = async columnKey => {
  const list = columnsState.value[columnKey] || [];
  const sectionId = columnKey === UNCLASSIFIED_KEY ? null : Number(columnKey);
  const items = list.map((tpl, idx) => ({
    template_name: tpl.name,
    section_id: sectionId,
    position: idx,
  }));
  // eslint-disable-next-line no-console
  console.log('[Templates] reorder column', columnKey, items);
  await store.dispatch('templateClassifications/reorder', {
    inboxId: Number(selectedInboxId.value),
    items,
  });
};

// ============================================================
// Drag & drop nativo estilo kanban (referencia Hover.dev / TomIsLoading):
// tarjetas draggable, indicador de posición entre tarjetas, resaltado de
// columna y opacidad en la tarjeta arrastrada. Sin librerías externas.
// ============================================================
const draggedCard = ref(null); // { name, fromCol }
const activeCol = ref(null);

const getIndicators = columnEl =>
  Array.from(columnEl.querySelectorAll('[data-drop-indicator]'));

const clearIndicators = indicators => {
  indicators.forEach(el => {
    el.classList.add('opacity-0');
    el.classList.remove('opacity-100');
  });
};

const getNearestIndicator = (event, indicators) => {
  const OFFSET = 50;
  return indicators.reduce(
    (closest, child) => {
      const box = child.getBoundingClientRect();
      const offset = event.clientY - (box.top + OFFSET);
      if (offset < 0 && offset > closest.offset) {
        return { offset, element: child };
      }
      return closest;
    },
    {
      offset: Number.NEGATIVE_INFINITY,
      element: indicators[indicators.length - 1],
    }
  );
};

const handleDragStart = (element, colKey) => {
  draggedCard.value = { name: element.name, fromCol: colKey };
};

const handleDragEnd = () => {
  draggedCard.value = null;
  activeCol.value = null;
  document
    .querySelectorAll('[data-drop-indicator]')
    .forEach(el => el.classList.add('opacity-0'));
};

const handleDragOver = (event, colKey) => {
  if (!draggedCard.value) return;
  event.preventDefault();
  activeCol.value = colKey;
  const indicators = getIndicators(event.currentTarget);
  clearIndicators(indicators);
  const { element } = getNearestIndicator(event, indicators);
  if (element) {
    element.classList.remove('opacity-0');
    element.classList.add('opacity-100');
  }
};

const handleDragLeave = event => {
  clearIndicators(getIndicators(event.currentTarget));
  activeCol.value = null;
};

const handleDrop = (event, colKey) => {
  event.preventDefault();
  const indicators = getIndicators(event.currentTarget);
  const { element } = getNearestIndicator(event, indicators);
  clearIndicators(indicators);
  activeCol.value = null;

  const dragged = draggedCard.value;
  draggedCard.value = null;
  if (!dragged) return;

  const before = element?.dataset.before || '-1';
  if (before === dragged.name) return; // soltada en su misma posición

  const sourceList = columnsState.value[dragged.fromCol];
  const idx = sourceList.findIndex(c => c.name === dragged.name);
  if (idx === -1) return;
  const [card] = sourceList.splice(idx, 1);
  card.section_id = colKey === UNCLASSIFIED_KEY ? null : Number(colKey);

  const destList = columnsState.value[colKey];
  if (before === '-1') {
    destList.push(card);
  } else {
    const insertAt = destList.findIndex(c => c.name === before);
    destList.splice(insertAt === -1 ? destList.length : insertAt, 0, card);
  }

  // Persistir: la columna destino siempre; la origen también si cambió.
  onColumnChange(colKey);
  if (dragged.fromCol !== colKey) onColumnChange(dragged.fromCol);
};

const handleWsRefresh = () => {
  // eslint-disable-next-line no-console
  console.log('[Templates] WS refresh — rebuilding columns');
  rebuildColumns();
};

watch(selectedInboxId, async () => {
  await fetchTemplates();
  rebuildColumns();
});

onMounted(async () => {
  if (!whatsappInboxes.value.length) {
    await store.dispatch('inboxes/get');
  }
  const first = whatsappInboxes.value[0];
  if (first) selectedInboxId.value = String(first.id);
  emitter.on('TEMPLATE_CLASSIFICATIONS_UPDATED', handleWsRefresh);
  store.watch(
    () =>
      selectedInboxId.value
        ? store.getters['templateClassifications/getTemplates'](
            Number(selectedInboxId.value)
          )
        : [],
    () => rebuildColumns(),
    { deep: true }
  );
});

onBeforeUnmount(() => {
  emitter.off('TEMPLATE_CLASSIFICATIONS_UPDATED', handleWsRefresh);
});
</script>

<template>
  <div class="flex flex-col w-full h-full p-6 overflow-hidden">
    <header class="flex items-center justify-between mb-4 flex-none">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('TEMPLATES_PAGE.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ t('TEMPLATES_PAGE.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex items-center gap-3">
        <!-- Desplegable de bandeja: muestra la bandeja seleccionada -->
        <SelectMenu
          v-if="inboxOptions.length"
          v-model="selectedInboxId"
          :options="inboxOptions"
          :label="selectedInboxLabel"
          sub-menu-position="bottom"
        />
        <input
          v-model="search"
          type="text"
          :placeholder="t('TEMPLATES_PAGE.SEARCH_PLACEHOLDER')"
          class="rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-sm text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:border-n-brand"
          @input="rebuildColumns"
        />
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :disabled="isRefreshing"
          :title="t('TEMPLATES_PAGE.REFRESH_TOOLTIP')"
          @click="refreshFromTwilio"
        >
          <span
            class="size-3.5"
            :class="
              isRefreshing
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-refresh-cw'
            "
          />
          {{
            isRefreshing
              ? t('TEMPLATES_PAGE.REFRESHING')
              : t('TEMPLATES_PAGE.REFRESH_BUTTON')
          }}
        </button>
        <span
          v-if="isUpdating"
          class="text-xs text-n-slate-10 inline-flex items-center gap-1"
        >
          <span class="i-lucide-loader-2 size-3 animate-spin" />
          {{ t('TEMPLATES_PAGE.SAVING') }}
        </span>
      </div>
    </header>

    <div
      v-if="isLoading"
      class="flex-1 flex items-center justify-center text-n-slate-10"
    >
      <span class="i-lucide-loader-2 size-5 animate-spin mr-2" />
      {{ t('TEMPLATES_PAGE.LOADING') }}
    </div>

    <div v-else class="flex-1 flex gap-4 overflow-x-auto pb-2">
      <div
        v-for="(col, colIndex) in columns"
        :key="col.key"
        class="flex flex-col w-80 shrink-0 rounded-xl border border-n-weak bg-n-alpha-1 overflow-hidden"
      >
        <div
          class="flex items-center justify-between gap-1 px-3 py-2 border-b border-n-weak bg-n-background"
        >
          <!-- Nombre de sección: editable al click (solo admin, no Unclassified) -->
          <input
            v-if="col.id && editingSectionId === col.id"
            :ref="focusInput"
            v-model="sectionEditValue"
            type="text"
            class="flex-1 min-w-0 text-sm font-semibold text-n-slate-12 bg-n-alpha-1 border border-n-brand rounded px-1.5 py-0.5 outline-none"
            @keyup.enter="saveSectionEdit(col)"
            @keyup.esc="cancelSectionEdit"
            @blur="saveSectionEdit(col)"
          />
          <h2
            v-else
            class="flex-1 min-w-0 text-sm font-semibold text-n-slate-12 truncate"
            :class="{ 'cursor-text hover:text-n-brand': isAdmin && col.id }"
            :title="
              isAdmin && col.id
                ? t('TEMPLATES_PAGE.SECTION_RENAME_HINT')
                : col.name
            "
            @click="startSectionEdit(col)"
          >
            {{ col.name }}
          </h2>
          <div class="flex items-center gap-1">
            <!-- Reordenar secciones (solo admin, no Unclassified) -->
            <template v-if="isAdmin && col.id">
              <button
                v-if="colIndex > 1"
                type="button"
                class="w-9 h-9 shrink-0 inline-flex items-center justify-center rounded-md border border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-2 hover:border-n-strong transition-colors"
                :title="t('TEMPLATES_PAGE.SECTION_MOVE_LEFT')"
                @click="moveSection(col, -1)"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
              <button
                v-if="colIndex < columns.length - 1"
                type="button"
                class="w-9 h-9 shrink-0 inline-flex items-center justify-center rounded-md border border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-2 hover:border-n-strong transition-colors"
                :title="t('TEMPLATES_PAGE.SECTION_MOVE_RIGHT')"
                @click="moveSection(col, 1)"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
              <button
                type="button"
                class="w-9 h-9 shrink-0 inline-flex items-center justify-center rounded-md border border-n-ruby-5 bg-n-background text-n-ruby-11 hover:bg-n-ruby-3 hover:border-n-ruby-8 transition-colors"
                :title="t('TEMPLATES_PAGE.SECTION_DELETE')"
                @click="askDeleteSection(col)"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M3 6h18" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
            </template>
            <span
              class="text-xs text-n-slate-10 rounded-full bg-n-alpha-2 px-2 py-0.5"
            >
              {{ (columnsState[col.key] || []).length }}
            </span>
          </div>
        </div>

        <div
          class="flex-1 p-2 overflow-y-auto min-h-[100px] transition-colors"
          :class="{ 'bg-n-alpha-2': activeCol === col.key }"
          @dragover="handleDragOver($event, col.key)"
          @dragleave="handleDragLeave($event)"
          @drop="handleDrop($event, col.key)"
        >
          <template
            v-for="element in columnsState[col.key] || []"
            :key="element.name"
          >
            <!-- Indicador de posición antes de esta tarjeta -->
            <div
              data-drop-indicator
              :data-before="element.name"
              class="h-1 -my-px rounded-full bg-n-brand opacity-0 transition-opacity duration-150"
            />
            <div
              :draggable="editingName !== element.name"
              class="rounded-lg border border-n-weak bg-n-background p-3 my-1 cursor-grab active:cursor-grabbing hover:border-n-brand transition-all"
              :class="{
                'opacity-40': draggedCard && draggedCard.name === element.name,
              }"
              @dragstart="handleDragStart(element, col.key)"
              @dragend="handleDragEnd"
            >
              <!-- Nombre: alias editable al click (solo admin) -->
              <input
                v-if="editingName === element.name"
                :ref="focusInput"
                v-model="editValue"
                type="text"
                class="w-full text-sm font-medium text-n-slate-12 bg-n-alpha-1 border border-n-brand rounded px-1.5 py-0.5 outline-none"
                :placeholder="element.friendly_name || element.name"
                @keyup.enter="saveEdit(element)"
                @keyup.esc="cancelEdit"
                @blur="saveEdit(element)"
                @mousedown.stop
                @click.stop
              />
              <p
                v-else
                class="text-sm font-medium text-n-slate-12 truncate"
                :class="{ 'cursor-text hover:text-n-brand': isAdmin }"
                :title="
                  isAdmin
                    ? t('TEMPLATES_PAGE.RENAME_HINT')
                    : displayNameOf(element)
                "
                @click.stop="startEdit(element)"
              >
                {{ displayNameOf(element) }}
                <span
                  v-if="element.display_name"
                  class="ms-1 text-[10px] font-normal text-n-slate-9"
                >
                  ({{ element.name }})
                </span>
              </p>
              <p
                v-if="element.body"
                class="mt-1 text-xs text-n-slate-10 line-clamp-2"
              >
                {{ element.body }}
              </p>
              <div
                v-if="element.language"
                class="mt-2 inline-flex items-center text-[10px] uppercase tracking-wide text-n-slate-9"
              >
                {{ element.language }}
              </div>
            </div>
          </template>
          <!-- Indicador de posición al final de la columna -->
          <div
            data-drop-indicator
            data-before="-1"
            class="h-1 -my-px rounded-full bg-n-brand opacity-0 transition-opacity duration-150"
          />
        </div>
      </div>

      <!-- Botón + : crear una nueva sección personalizada (solo admin) -->
      <div v-if="isAdmin" class="w-72 shrink-0">
        <div
          v-if="isAddingSection"
          class="rounded-xl border border-n-brand bg-n-alpha-1 p-3"
        >
          <input
            :ref="focusInput"
            v-model="newSectionName"
            type="text"
            :placeholder="t('TEMPLATES_PAGE.SECTION_NAME_PLACEHOLDER')"
            class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-sm text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:border-n-brand"
            @keyup.enter="createSection"
            @keyup.esc="isAddingSection = false"
          />
          <div class="flex items-center justify-end gap-2 mt-2">
            <button
              type="button"
              class="rounded-lg px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2"
              @click="isAddingSection = false"
            >
              {{ t('TEMPLATES_PAGE.SECTION_CANCEL') }}
            </button>
            <button
              type="button"
              class="rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white hover:brightness-110 disabled:opacity-50"
              :disabled="!newSectionName.trim()"
              @click="createSection"
            >
              {{ t('TEMPLATES_PAGE.SECTION_CREATE') }}
            </button>
          </div>
        </div>
        <button
          v-else
          type="button"
          class="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-n-weak text-sm text-n-slate-10 hover:text-n-slate-12 hover:border-n-brand hover:bg-n-alpha-1 transition-colors"
          :title="t('TEMPLATES_PAGE.SECTION_ADD')"
          @click="isAddingSection = true"
        >
          <span class="i-lucide-plus size-4" />
          {{ t('TEMPLATES_PAGE.SECTION_ADD') }}
        </button>
      </div>
    </div>

    <!-- Confirmación de borrado de sección -->
    <Dialog
      ref="deleteSectionDialogRef"
      type="alert"
      :title="
        t('TEMPLATES_PAGE.SECTION_DELETE_TITLE', {
          name: sectionToDelete?.name || '',
        })
      "
      :description="t('TEMPLATES_PAGE.SECTION_DELETE_DESCRIPTION')"
      :confirm-button-label="t('TEMPLATES_PAGE.SECTION_DELETE')"
      @confirm="confirmDeleteSection"
    />
  </div>
</template>
